import React from 'react';
import { Button } from '../../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { 
  ChevronLeft, 
  Shuffle, 
  Pause, 
  Play, 
  RotateCcw,
  Lightbulb
} from 'lucide-react';
import { cn } from '../../../lib/utils';

export function GameActionButtons({
  onPrevious,
  onNewImage,
  onPause,
  onReset,
  onHint,
  canGoBack,
  hasNewImages,
  isPaused,
  isRunning,
  hasUnplacedPieces,
  isComplete,
  isLandscape = false,
  variant = 'desktop'
}) {
  const isCompact = variant === 'landscape';
  const buttonSize = isCompact ? "h-7 w-7" : "h-10 w-10";
  const iconSize = isCompact ? "h-3 w-3" : "h-4 w-4";
  const iconSizeLarge = isCompact ? "h-3.5 w-3.5" : "h-5 w-5";
  
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button 
            variant="outline" 
            size="icon"
            onClick={onPrevious}
            className={cn(buttonSize, !canGoBack && "opacity-50")}
            disabled={!canGoBack}
          >
            <ChevronLeft className={iconSizeLarge} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{canGoBack ? 'Previous Image' : 'No previous images'}</TooltipContent>
      </Tooltip>
      
      <Tooltip>
        <TooltipTrigger asChild>
          <Button 
            variant="outline" 
            size="icon"
            onClick={onNewImage}
            className={cn(buttonSize, !hasNewImages && "opacity-50")}
          >
            <Shuffle className={iconSize} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{hasNewImages ? 'New Image' : 'No new images available'}</TooltipContent>
      </Tooltip>
      
      {variant === 'desktop' && <div className="w-px h-8 bg-border mx-1" />}
      
      <Tooltip>
        <TooltipTrigger asChild>
          <Button 
            variant={isPaused ? "default" : "outline"}
            size="icon"
            onClick={onPause}
            disabled={!isRunning}
            className={buttonSize}
          >
            {isPaused ? <Play className={iconSize} /> : <Pause className={iconSize} />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{isPaused ? 'Resume' : 'Pause'}</TooltipContent>
      </Tooltip>
      
      <Tooltip>
        <TooltipTrigger asChild>
          <Button 
            variant="outline" 
            size="icon"
            onClick={onReset}
            className={buttonSize}
          >
            <RotateCcw className={iconSize} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Reset Puzzle</TooltipContent>
      </Tooltip>
      
      {variant === 'desktop' && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="outline" 
              size="icon"
              onClick={onHint}
              disabled={!hasUnplacedPieces || isComplete || isPaused}
              className={cn(
                buttonSize,
                (!hasUnplacedPieces || isComplete || isPaused) && "opacity-50"
              )}
              data-testid="hint-btn"
            >
              <Lightbulb className={iconSize} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Hint (-25 points)</TooltipContent>
        </Tooltip>
      )}
    </TooltipProvider>
  );
}
